import React, { useState } from 'react';

import {
  Box,
  Button,
  FormikField,
  H2,
  Input,
  Label,
  ListRow,
  RadioGroup,
  Separator,
  Text,
} from '@betterment/samba-react';
import { useFeatureFlag } from '@betterment/shared-components';
import { useFormikContext } from 'formik';
import * as Yup from 'yup';

import { PlatformFeeTier } from '../../../../graphql/schema';
import { parseFloatFromNumberInputMask } from '../../../../utils/input-helpers';
import { PlatformFeePaidBy } from '../data';
import PlatformFeeExplanationModal from '../platform-fee-explanation-modal/PlatformFeeExplanationModal';

export const platformFeeNettingSchema = (platformFee: number) => {
  return Yup.object().shape({
    platformFeePaidBy: Yup.string()
      .oneOf(Object.values(PlatformFeePaidBy), 'Must select a valid option')
      .required('Platform fee paid by is required'),
    sharedPlatformFeeClientPortionBps: Yup.number()
      .transform((_, originalValue: string | null) => {
        if (originalValue === '' || originalValue === null) {
          return null;
        }
        const parsedValue = parseFloatFromNumberInputMask(originalValue);
        return Number.isNaN(parsedValue) ? null : parsedValue;
      })
      .when('platformFeePaidBy', {
        is: PlatformFeePaidBy.Shared,
        then: schema =>
          schema
            .required('Fee rate is required')
            .integer('Must be an integer')
            .min(1, '1 bps minimum')
            .max(platformFee - 1, `${platformFee - 1} bps maximum`),
        otherwise: schema => schema.nullable(),
      }),
  });
};

type PlatformFeeBoxProps = {
  platformFee: number;
  tiers: PlatformFeeTier[];
};

export const PlatformFeeBox: React.FC<PlatformFeeBoxProps> = ({ platformFee, tiers }) => {
  const [isModalOpen, setModalOpen] = useState<boolean>(false);
  const { values } = useFormikContext<{
    platformFeePaidBy: PlatformFeePaidBy;
  }>();
  const [platformFeePaidBy, setPlatformFeePaidBy] = useState<PlatformFeePaidBy>(values.platformFeePaidBy);

  const { enabled: isPlatformFeeNettingEnabled, loading } = useFeatureFlag({
    splitName: 'retail.b4a_platform_fee_netting_q3_2025_enabled',
    context: 'BillingPlanFlow',
  });

  const isSharedPlatformFee = platformFeePaidBy === PlatformFeePaidBy.Shared;

  return (
    <Box
      css={{
        borderWidth: 'thin',
        borderStyle: 'solid',
        borderColor: 'border',
        borderRadius: 'medium',
        padding: 'space.300',
      }}
    >
      <ListRow css={{ padding: 'space.0' }}>
        <ListRow.LeadingContent css={{ gap: 'space.50' }}>
          <H2 id="betterment-platform-fee" variant="title200">
            Betterment platform fee
          </H2>
          <Text color="text.weak" variant="text50">
            This fee accrues daily and is billed at the same frequency as your firm’s advisory fee. It may vary for
            clients depending on their AUM on the platform.
          </Text>
          {tiers.length > 0 && (
            <Text color="text.weak" variant="text50">
              <Button variant="textSecondary" onClick={() => setModalOpen(true)}>
                Learn more
              </Button>
            </Text>
          )}
        </ListRow.LeadingContent>
        <ListRow.TrailingContent css={{ marginLeft: 'space.600' }}>
          <Input
            aria-labelledby="betterment-platform-fee"
            alignment="right"
            css={{
              width: '160px',
              border: 'none',
            }}
            readOnly={true}
            value={`${platformFee} bps / year`}
          />
        </ListRow.TrailingContent>
      </ListRow>
      <PlatformFeeExplanationModal isOpen={isModalOpen} closeHandler={() => setModalOpen(false)} rows={tiers} />
      {isPlatformFeeNettingEnabled && !loading && (
        <Box>
          <Separator css={{ marginY: 'space.200' }} />
          <FormikField name="platformFeePaidBy">
            <FormikField.Control>
              <RadioGroup
                onValueChange={(value: PlatformFeePaidBy) => {
                  void setPlatformFeePaidBy(value);
                }}
                value={platformFeePaidBy}
                aria-labelledby="configurationTypeGroupLabel"
                css={{ gap: '0' }}
              >
                <Label css={{ pb: 'space.50' }}>Who will pay the platform fee?</Label>
                <RadioGroup.ItemContainer css={{ pt: 'space.200' }}>
                  <RadioGroup.Item value={PlatformFeePaidBy.ClientOnly} />
                  <RadioGroup.ItemLabel>Client</RadioGroup.ItemLabel>
                </RadioGroup.ItemContainer>
                <RadioGroup.ItemContainer css={{ pt: 'space.150' }}>
                  <RadioGroup.Item value={PlatformFeePaidBy.AdvisorOnly} />
                  <RadioGroup.ItemLabel>Your firm</RadioGroup.ItemLabel>
                </RadioGroup.ItemContainer>
                <RadioGroup.ItemContainer css={{ pt: 'space.150' }}>
                  <RadioGroup.Item value={PlatformFeePaidBy.Shared} />
                  <RadioGroup.ItemLabel>Shared</RadioGroup.ItemLabel>
                </RadioGroup.ItemContainer>
              </RadioGroup>
            </FormikField.Control>
            <FormikField.ErrorMessage />
          </FormikField>
          {isSharedPlatformFee && (
            <Box>
              <Separator css={{ marginY: 'space.200' }} />
              <FormikField name="sharedPlatformFeeClientPortionBps">
                <ListRow css={{ padding: '0' }}>
                  <ListRow.LeadingContent>
                    <FormikField.Label>
                      <Text as="div" variant="text100">
                        How much will your client pay?
                      </Text>
                      <Text color="text.weak" variant="text50">
                        If the platform fee changes, the client’s portion remains the same.
                      </Text>
                    </FormikField.Label>
                  </ListRow.LeadingContent>
                  <ListRow.TrailingContent>
                    <FormikField.Control>
                      <Input aria-label="client portion" mask="number" trailingSymbol="bps" alignment="right" />
                    </FormikField.Control>
                    <FormikField.ErrorMessage />
                  </ListRow.TrailingContent>
                </ListRow>
              </FormikField>
            </Box>
          )}
        </Box>
      )}
    </Box>
  );
};
